<template>
  <div class="contact-us-form">
    <el-form class="message" ref="messageFormRef" :model="messageData" :rules="messageRules">
      <el-row :gutter="24">
        <el-col :sm="24" :md="12">
          <el-form-item
            prop="userName"
            :class="{ 'label-float': isNameFocused || messageData.userName }"
            :label="t('validate.name')"
          >
            <el-input
              class="mess-input"
              v-model="messageData.userName"
              @focus="isNameFocused = true"
              @blur="isNameFocused = false"
              :maxlength="20"
            />
          </el-form-item>
        </el-col>
        <el-col :sm="24" :md="12">
          <el-form-item
            prop="mobile"
            :class="{ 'label-float': isPhoneFocused || messageData.mobile }"
            :label="t('validate.tel')"
          >
            <el-input
              class="mess-input"
              v-model="messageData.mobile"
              @focus="isPhoneFocused = true"
              @blur="isPhoneFocused = false"
              :maxlength="11"
              @input="messageData.mobile = messageData.mobile.replace(/\D/g, '')"
            />
          </el-form-item>
        </el-col>
        <el-col :sm="24">
          <el-form-item
            prop="companyName"
            :class="{ 'label-float': isCompanyFocused || messageData.companyName }"
            :label="t('validate.company')"
          >
            <el-input
              class="mess-input"
              v-model="messageData.companyName"
              @focus="isCompanyFocused = true"
              @blur="isCompanyFocused = false"
              :maxlength="60"
            />
          </el-form-item>
        </el-col>
        <el-col :sm="24">
          <el-form-item
            prop="remark"
            class="content"
            :class="{ 'label-float': isContentFocused || messageData.remark }"
            :label="t('validate.inputQuestion')"
          >
            <el-input
              class="mess-input"
              type="textarea"
              :maxlength="100"
              v-model="messageData.remark"
              show-word-limit
              @focus="isContentFocused = true"
              @blur="isContentFocused = false"
            />
          </el-form-item>
        </el-col>
        <el-col :sm="24">
          <el-form-item class="margin-0">
            <div class="mess-btn btn-default auto" @click="submitForm(messageFormRef)">{{ t('validate.submitNow') }}</div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { SignatureUtil } from '@/utils/signature'
import useServerRequest from '@/composables/useServerRequest'
import type { configResponse } from '~/types'
import { useI18n } from 'vue-i18n' // 导入 useI18n
const { locale, t } = useI18n() // 获取当前 i18n 语言（如 zh-CN/en-US）
const config = useRuntimeConfig()
const cmsurlStrategy = config?.public?.cmsStrategy

const props = defineProps({
  apiUrl: {
    type: String,
    default: ''
  }
})

const isNameFocused = ref(false)
const isPhoneFocused = ref(false)
const isCompanyFocused = ref(false)
const isContentFocused = ref(false)
interface messageForm {
  userName: string
  mobile: string
  companyName: string
  remark: string
}
const messageFormRef = ref<FormInstance>()
const messageData = reactive<messageForm>({
  userName: '',
  mobile: '',
  companyName: '',
  remark: ''
})

// 手机号验证规则
const validatePhone = (rule: any, value: string, callback: any) => {
  if (!value) {
    callback(new Error(t('validate.tel')))
  } else if (!/^1[3-9]\d{9}$/.test(value)) {
    callback(new Error(t('validate.tel')))
  } else {
    callback()
  }
}
const messageRules = reactive<FormRules<messageForm>>({
  userName: [{ required: true, message: t('validate.name'), trigger: 'blur' }],
  mobile: [{ required: true, validator: validatePhone, trigger: 'blur' }],
  companyName: [{ required: true, message: t('validate.company'), trigger: 'blur' }]
})
const getSign = (params: messageForm) => {
  const headers = {
    appId: 'moji',
    timestamp: SignatureUtil.getTimestamp(),
    nonce: SignatureUtil.generateNonce()
  } as any
  const bodyString = JSON.stringify(params)
  const sign = SignatureUtil.generateSignature(
    {}, // 你的接口没有 query params
    bodyString, // body 原始字符串
    headers, // 参与签名的 header
    'c309d36bbe39' // 密钥
  )
  headers['sign'] = sign
  return headers
}
const emit = defineEmits(['success', 'submit'])
const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return
  await formEl.validate(async (valid, fields) => {
    if (valid) {
      const params = JSON.parse(JSON.stringify(messageData))
      const formData = new FormData()
      Object.keys(params).forEach((key) => {
        formData.append(key, params[key])
      })
      const loading = ElLoading.service({
        lock: true,
        background: 'rgba(0, 0, 0, 0.5)'
      })
      emit('submit')

      try {
        const { data } = await useServerRequest<configResponse>(props.apiUrl + SignatureUtil.getTimestamp(), `${cmsurlStrategy}/${props.apiUrl}`, {
          method: 'POST',
          params: formData as any,
          headers: {
            ...getSign(params),
          },
          language: locale.value
        })
        const resData = data?.value
        if (resData?.data) {
          ElMessage.success(t('validate.submitSuccess'))
          // 清空表单数据
          messageData.userName = ''
          messageData.mobile = ''
          messageData.companyName = ''
          messageData.remark = ''
          emit('success')
        } else {
          ElMessage.error(resData?.msg || t('validate.submitFail'))
        }
      } catch (error) {
      } finally {
        loading.close()
      }
    } else {
      console.log('error submit!', fields)
    }
  })
}
</script>

<style lang="scss" scoped>
.message {
  --el-component-size: 58px;
  .el-form-item {
    position: relative;
    margin-bottom: 24px;
    --el-font-size-base: 16px;
    --el-border-radius-base: 0;
    --el-fill-color-blank: rgba(255, 255, 255, 0.1);
    --el-text-color-regular: #ffffff;
    :deep(.el-form-item__error) {
      font-size: 14px;
      color: var(--org-color);
      padding-top: 4px;
    }
    &.is-required {
      &::before {
        display: block;
        content: '*';
        position: absolute;
        left: 24px;
        top: 3px;
        color: var(--org-color);
        line-height: var(--el-component-size);
      }
    }
    &.margin-0 {
      margin: 0;
    }
  }
  :deep(.el-form-item__label) {
    position: absolute;
    top: 50%;
    left: 36px;
    font-size: var(--el-font-size-base);
    color: #ffffff;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
    &::before {
      display: none;
    }
  }
  :deep(.content .el-form-item__label) {
    top: 16px;
    left: 24px;
    transform: translateY(0);
  }
  :deep(.label-float .el-form-item__label) {
    opacity: 0;
    visibility: hidden;
  }
  .el-form-item:not(.is-error) {
    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px rgba($color: #000000, $alpha: 0) inset;
    }
  }
  .mess-input {
    width: 100%;
    :deep(.el-input__wrapper) {
      padding: 1px 24px;
      padding-left: 36px;
      &:hover,
      &.is-focus {
        box-shadow: 0 0 0 1px rgba($color: #FFFFFF, $alpha: 0.3) inset;
      }
    }
    :deep(.el-textarea__inner) {
      padding: 16px 24px;
      resize: none;
      height: 116px;
      box-shadow: none;
      &:hover,
      &.is-focus {
        box-shadow: 0 0 0 1px rgba($color: #FFFFFF, $alpha: 0.3) inset;
      }
    }
    :deep(.el-input__count){
      right: 5px;
      background: transparent;
      font-size: 14px;
      color: rgba($color: #FFFFFF, $alpha: 0.3);
    }
  }
  .mess-btn {
    margin-top: 16px;
  }
}

@media (max-width: 767px) {
  .message {
    --el-component-size: 44px;
    .el-form-item {
      margin-bottom: 18px;
      --el-font-size-base: 14px;
      &.is-required {
        &::before {
          left: 10px;
        }
      }
      :deep(.el-form-item__error) {
        font-size: 12px;
        padding-top: 2px;
      }
    }
    .mess-input {
      :deep(.el-input__wrapper) {
        padding: 1px 15px;
        padding-left: 20px;
      }
      :deep(.el-textarea__inner) {
        padding: 10px 15px;
      }
    }
    .mess-btn {
      margin-top: 5px;
    }
    :deep(.el-form-item__label) {
      left: 20px;
    }
    :deep(.content .el-form-item__label) {
      top: 10px;
      left: 15px;
      line-height: 1.5;
    }
  }
}
</style>
